import { useState } from 'react';
import useBorradoProgreso from './useBorradoProgreso';
import { exportarAExcel } from './exportarExcelHelper';
import { formatearFechaLarga } from './formatters';

export const useAccionesBorrado = ({
    token,
    isAdmin,
    data,
    showSuccessToast,
    showErrorToast
}) => {
    const {
        licenciasSeleccionadas,
        setLicenciasSeleccionadas,
        candidatasTotal,
        itemsFiltrados,
        estatusBorrado,
        mesesVencimiento,
        pagina,
        tamanioPagina,
        tipoLicencia,
        ordenDias,
        setCargando,
        cargarDatosServidor,
        conteoTabs,
        resumen
    } = data;

    const {
        setProgresoBorrado,
        idsEnBorradoRef,
        iniciarPollingProgreso
    } = useBorradoProgreso();

    // Modal de confirmación
    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null,
        variant: 'danger',
        confirmText: 'Confirmar'
    });

    const cerrarModalConfirmacion = () => {
        setModalConfirm(prev => ({ ...prev, show: false }));
    };

    const solicitarConfirmacion = (title, message, variant, confirmText, onConfirm) => {
        setModalConfirm({
            show: true,
            title,
            message,
            onConfirm,
            variant,
            confirmText
        });
    };

    const recargar = () => {
        cargarDatosServidor(mesesVencimiento, pagina, tamanioPagina, tipoLicencia, ordenDias, estatusBorrado);
    };

    // 1. Programar borrado
    const handleEliminarSeleccionadas = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden programar el borrado de empresas.');
            return;
        }
        if (licenciasSeleccionadas.length === 0) return;

        const cantidad = licenciasSeleccionadas.length;
        solicitarConfirmacion(
            'Programar Borrado',
            `¿Estás seguro de que deseas programar el borrado de las ${cantidad} empresas seleccionadas?`,
            'primary',
            'Programar Borrado',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: licenciasSeleccionadas.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || '',
                            bIsActive: true
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/EliminarDataLicenciasVencidas', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast(cantidad === 1 ? 'Empresa programada correctamente.' : 'Empresas programadas correctamente.');
                        setLicenciasSeleccionadas([]);
                        recargar();
                    } else {
                        showErrorToast(`Error al programar: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    // 2. Quitar del borrado
    const handleQuitarBorrado = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden quitar empresas del borrado.');
            return;
        }
        if (licenciasSeleccionadas.length === 0) return;

        solicitarConfirmacion(
            'Quitar del Borrado',
            `¿Estás seguro de que deseas quitar ${licenciasSeleccionadas.length} empresas del borrado programado?`,
            'warning',
            'Quitar del Borrado',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: licenciasSeleccionadas.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || '',
                            bIsActive: false
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/EliminarDataLicenciasVencidas', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast('Empresas quitadas del borrado correctamente.');
                        setLicenciasSeleccionadas([]);
                        recargar();
                    } else {
                        showErrorToast(`Error al quitar del borrado: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    // 3. Reintentar borrado
    const handleReintentarBorrado = (licenciaUnica = null) => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden reactivar empresas para borrado.');
            return;
        }
        const seleccion = licenciaUnica ? [licenciaUnica] : licenciasSeleccionadas;
        if (seleccion.length === 0) return;

        solicitarConfirmacion(
            'Reintentar / Volver a Programar',
            `¿Estás seguro de que deseas reactivar y volver a programar el borrado de ${seleccion.length === 1 ? 'esta empresa' : `las ${seleccion.length} empresas seleccionadas`}?`,
            'primary',
            'Reintentar y Programar',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: seleccion.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || '',
                            bIsActive: true
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/EliminarDataLicenciasVencidas', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast(seleccion.length === 1 ? 'Empresa reactivada correctamente.' : 'Empresas reactivadas correctamente.');
                        setLicenciasSeleccionadas(prev => prev.filter(sel => !seleccion.some(s => s.idLicencia === sel.idLicencia)));
                        recargar();
                    } else {
                        showErrorToast(`Error al reactivar: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    // 4. Descartar no borradas
    const handleDescartarNoBorradas = (licenciaUnica = null) => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden descartar empresas.');
            return;
        }
        const seleccion = licenciaUnica ? [licenciaUnica] : licenciasSeleccionadas;
        if (seleccion.length === 0) return;

        solicitarConfirmacion(
            'Descartar / Quitar',
            `¿Estás seguro de que deseas descartar y quitar ${seleccion.length === 1 ? 'esta empresa' : `las ${seleccion.length} empresas seleccionadas`} de la lista?`,
            'danger',
            'Descartar / Quitar',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: seleccion.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || '',
                            bIsActive: false,
                            bDescartar: true
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/DescartarEmpresasABorrar', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast(seleccion.length === 1 ? 'Empresa descartada correctamente.' : 'Empresas descartadas correctamente.');
                        setLicenciasSeleccionadas(prev => prev.filter(sel => !seleccion.some(s => s.idLicencia === sel.idLicencia)));
                        recargar();
                    } else {
                        showErrorToast(`Error al descartar empresas: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    // 5. Ejecutar borrado inmediato
    const handleEjecutarBorradoInmediato = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden ejecutar el servicio de borrado.');
            return;
        }

        const totalProgramadas = (conteoTabs && conteoTabs[1] !== undefined)
            ? Number(conteoTabs[1])
            : Number(resumen?.iTotalProgramadas || 0);

        if (totalProgramadas <= 0) {
            showErrorToast('No hay empresas programadas para borrado.');
            return;
        }

        solicitarConfirmacion(
            'Ejecutar Borrado Inmediato',
            '¿Estás seguro de que deseas ejecutar el servicio de borrado de bases de datos ahora mismo? Antes de borrar, se validará individualmente cada empresa para garantizar que ninguna con licencias vigentes de Nube o AkasiaPOS sea eliminada.',
            'danger',
            'Ejecutar Inmediatamente',
            async () => {
                setCargando(true);
                cerrarModalConfirmacion();

                const idsAProcesar = licenciasSeleccionadas.length > 0
                    ? licenciasSeleccionadas.map(l => l.idLicencia)
                    : (candidatasTotal || []).map(l => l.idLicencia);
                idsEnBorradoRef.current = idsAProcesar;

                setProgresoBorrado({
                    show: false,
                    enProgreso: true,
                    completado: false,
                    totalEmpresas: idsAProcesar.length || 0,
                    procesadas: 0,
                    exitosas: 0,
                    fallidas: 0,
                    empresaActual: null,
                    empresas: []
                });

                try {
                    const response = await fetch('/api/Mantenimiento/EjecutarBorradoInmediato', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.ok) {
                        const resData = await response.json();
                        if (resData.sinEmpresas) {
                            showErrorToast(resData.mensaje || 'No hay empresas programadas para borrado.');
                            setProgresoBorrado(prev => ({ ...prev, show: false, enProgreso: false }));
                            return;
                        }
                        if (resData.enEjecucion && Array.isArray(resData.idsLicencias) && resData.idsLicencias.length > 0) {
                            showSuccessToast('Ya hay un ciclo de borrado activo. Conectando al progreso actual...');
                            idsEnBorradoRef.current = resData.idsLicencias;
                            iniciarPollingProgreso(resData.idsLicencias);
                        } else {
                            showSuccessToast('Borrando empresas en segundo plano...');
                            iniciarPollingProgreso(idsAProcesar);
                        }
                    } else {
                        const errText = await response.text();
                        showErrorToast(`Error al iniciar servicio: ${errText || response.statusText}`);
                        setProgresoBorrado(prev => ({ ...prev, show: false, enProgreso: false }));
                    }
                } catch (error) {
                    showErrorToast(`Error de conexión: ${error.message}`);
                    setProgresoBorrado(prev => ({ ...prev, show: false, enProgreso: false }));
                } finally {
                    setCargando(false);
                }
            }
        );
    };

    // 6. Exportar Excel
    const handleExportarExcel = () => {
        const datosAExportar = licenciasSeleccionadas.length > 0 
            ? licenciasSeleccionadas 
            : (itemsFiltrados.length > 0 ? itemsFiltrados : candidatasTotal);

        exportarAExcel({
            datosAExportar,
            estatusBorrado,
            formatearFechaLarga,
            showSuccessToast,
            showErrorToast
        });
    };

    return {
        modalConfirm,
        cerrarModalConfirmacion,
        solicitarConfirmacion,
        handleEliminarSeleccionadas,
        handleQuitarBorrado,
        handleReintentarBorrado,
        handleDescartarNoBorradas,
        handleEjecutarBorradoInmediato,
        handleExportarExcel
    };
};

export default useAccionesBorrado;
